import React, { useEffect, useRef, useState } from 'react';
import { 
  Truck, 
  Heart, 
  Shield, 
  Zap, 
  GraduationCap, 
  BarChart3 
} from 'lucide-react';

const ITSolutionsSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log("Video autoplay failed:", error);
      });
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const getResponsiveStyle = (baseStyle, mobileStyle, tabletStyle) => {
    if (screenSize.isMobile) return { ...baseStyle, ...mobileStyle };
    if (screenSize.isTablet) return { ...baseStyle, ...tabletStyle };
    return baseStyle;
  };

  const styles = {
    section: getResponsiveStyle(
      {
        position: 'relative',
        height: '70vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      {
        height: '60vh',
      },
      {
        height: '65vh',
      }
    ),
    videoBackground: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      minWidth: '100%',
      minHeight: '100%',
      width: 'auto',
      height: 'auto',
      transform: 'translate(-50%, -50%)',
      zIndex: 1
    },
    video: getResponsiveStyle(
      {
        width: '100%',
        height: '70vh',
        objectFit: 'cover'
      },
      {
        height: '60vh',
      },
      {
        height: '65vh',
      }
    ),
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 100%)',
      zIndex: 2
    },
    contentWrapper: getResponsiveStyle(
      {
        position: 'relative',
        zIndex: 3,
        width: '100%',
        height: '100%',
        marginRight: '-64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 4%'
      },
      {
        justifyContent: 'center',
        marginRight: '0',
        padding: '0 15px'
      },
      {
        justifyContent: 'center',
        marginRight: '0',
        padding: '0 30px'
      }
    ),
    content: getResponsiveStyle(
      {
        width: '45%',
        maxWidth: '600px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5vh',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out'
      },
      {
        width: '95%',
        maxWidth: '500px',
        padding: '0 15px'
      },
      {
        width: '80%',
        maxWidth: '550px',
      }
    ),
    badge: {
      background: '#400684',
      color: 'white',
      padding: '0.6vh 1.2vh',
      borderRadius: '100px',
      fontSize: screenSize.isMobile 
        ? 'clamp(0.6rem, 2.5vw, 0.8rem)' 
        : 'clamp(0.7rem, 1.2vh, 0.9rem)',
      fontWeight: '600',
      width: 'fit-content',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.8s ease-out 0.2s'
    },
    title: {
      fontSize: screenSize.isMobile 
        ? 'clamp(1.5rem, 5vw, 2rem)' 
        : screenSize.isTablet 
        ? '28px' 
        : '32px',
      fontWeight: '700',
      color: '#400684',
      fontFamily: '"Oswald", sans-serif',
      lineHeight: '1.2',
      margin: '1.5vh 0',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.8s ease-out 0.3s'
    },
    description: {
      fontSize: screenSize.isMobile 
        ? 'clamp(0.8rem, 3.5vw, 1rem)' 
        : 'clamp(0.9rem, 1.8vh, 1.1rem)',
      color: 'black',
      lineHeight: '1.5',
      margin: '0 0 1.5vh 0',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'all 0.8s ease-out 0.4s'
    },
    servicesGrid: getResponsiveStyle(
      {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '0.8vh',
        width: '100%',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.8s ease-out 0.5s'
      },
      {
        gridTemplateColumns: '1fr 1fr',
        gap: '0.5vh'
      },
      {
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.6vh'
      }
    ),
    serviceItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
      padding: screenSize.isMobile ? '0.6vh' : '0.8vh',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '10px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    iconBox: {
      background: '#400684',
      borderRadius: '6px',
      padding: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    serviceName: {
      fontSize: screenSize.isMobile 
        ? 'clamp(0.7rem, 2.5vw, 0.8rem)' 
        : 'clamp(0.8rem, 1.6vh, 0.9rem)',
      fontWeight: '500',
      color: 'black'
    }
  };

  const services = [
    { name: 'Fleet Management', Icon: Truck },
    { name: 'Healthcare', Icon: Heart },
    { name: 'Security', Icon: Shield },
    { name: 'Energy', Icon: Zap },
    { name: 'Education', Icon: GraduationCap },
    { name: 'Analytics', Icon: BarChart3 },
  ];

  return (
    <section ref={sectionRef} style={styles.section}>
      <div style={styles.videoBackground}>
        <video
          ref={videoRef}
          style={styles.video}
          muted
          playsInline
          loop
          poster="/server.png"
        >
          <source src="/server.mp4" type="video/mp4" />
        </video>
      </div>
      
      <div style={styles.contentWrapper}>
        <div style={styles.content}>
          <span style={styles.badge}>Enterprise Solutions</span>
          
          <h1 style={styles.title}>
            NEXT-GEN IOT & CLOUD SOLUTIONS
          </h1>
          
          <p style={styles.description}>
            Empower your business with cutting-edge IoT and Cloud services. 
            Transform your operations with innovative technology solutions 
            designed for the future of enterprise.
          </p>
          
          <div style={styles.servicesGrid}>
            {services.map((service, index) => (
              <div 
                key={index} 
                style={styles.serviceItem}
              >
                <div style={styles.iconBox}>
                  <service.Icon 
                    size={screenSize.isMobile ? 14 : 16} 
                    color="white" 
                  />
                </div>
                <span style={styles.serviceName}>{service.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ITSolutionsSection;